import type { FC, JSX } from "react";

import { AllowedPartners } from "@expert/expert-university";
import { Accordion, Group, Paper, ScrollArea, Stack, Text } from "@mantine/core";
import { IconClipboardList } from "@tabler/icons-react";

import { AccordionSection as Section } from "@/common-ui/accordion/AccordionSection";
import { UncheckedRadioIcon } from "@/sales-tools/home-product/components/Icons";

import { isValidHttpUrl } from "../utils";

const partnerContent = {
    verizon: {
        sellClosingContent: [
            {
                sectionTitle: "Thank & Reinforce",
                sectionGroups: [
                    {
                        heading: (
                            <>
                                Leave a lasting positive impression by <b>briefly</b> celebrating the customer getting
                                Enrolled.
                            </>
                        ),
                        bulletPoints: [],
                    },
                ],
            },
            {
                sectionTitle: "Survey",
                sectionGroups: [
                    {
                        heading: "",
                        bulletPoints: [
                            "Be sure to use the approved verbiage for your team.",
                            "Thank the customer for taking the survey, “Thank you for taking the survey (CUSTOMER NAME), It really means a lot to me.”",
                        ],
                    },
                ],
            },
            {
                sectionTitle: "Goodbye with Rapport",
                sectionGroups: [
                    {
                        heading: "",
                        bulletPoints: [
                            "Wish the customer well on whatever rapport you built during the call.",
                            "E.g.: Let’s say you found out the customer was having dinner with friends tonight. The end of the call might sound something like this:",
                            "“Thank you for taking the survey, it means a lot to me. (CUSTOMER NAME), I’m so glad we got you squared away today! And I hope you and your friends have a great time at dinner tonight, have a wonderful rest of your day!",
                        ],
                    },
                ],
            },
        ],
        sellSmartOfferContent: [
            {
                sectionTitle: "Transition Statement",
                sectionGroups: [
                    {
                        heading:
                            "“I'd like to talk to you about a separate coverage from your mobile device insurance called Verizon Home Device Protect.”",
                        bulletPoints: [],
                    },
                ],
            },
            {
                sectionTitle: "Discovery Question",
                sectionGroups: [
                    {
                        heading: "Choose ONE of these options:",
                        bulletPoints: [
                            "“What types of products like, tablets, laptops, or gaming systems does your household use the most?”",
                            "“Have you or your family bought any new tech recently—anything you’re excited about?”​",
                            "“Is there any home tech you’re thinking about upgrading or replacing soon?”",
                        ],
                    },
                ],
            },
            {
                sectionTitle: "Engagement Question",
                sectionGroups: [
                    {
                        heading: "Choose ONE of these options:",
                        bulletPoints: [
                            "“Are those products getting daily use, and what are they mainly used for?”",
                            "“How do you and your family usually use that tech day to day?”​​",
                            "“What do you usually do when your tech stops working?”",
                        ],
                    },
                ],
            },
            {
                sectionTitle: "Core Benefits",
                sectionGroups: [
                    {
                        heading: "",
                        bulletPoints: [
                            "“This plan covers breakdowns on all eligible products for one monthly price no matter when or where they were purchased!”",
                            "“The products you currently own are covered and future purchases are automatically included; No receipts or registration required.”​​",
                            "“A service fee of no more than $99 plus tax is required per claim, and your home tech will be repaired, replaced, or reimbursed.”",
                        ],
                    },
                ],
            },
            {
                sectionTitle: "Examples of Coverage",
                sectionGroups: [
                    {
                        heading: "Choose 1 to 3 to share based on the products mentioned by the customer.",
                        bulletPoints: [
                            "“We’ll cover accidental damage from handling to your eligible laptops for things such as drops, spills, and cracked screens.”",
                            "“We’ll cover your eligible computers for mechanical or electrical failure, and our 24/7 tech support experts love to be a helping hand when you have issues!”",
                            "“We can repair, replace, or reimburse you for your tablet if it experiences a covered breakdown!”",
                            "“We’ll cover your eligible TVs for issues caused by mechanical or electrical failure, and our 24/7 tech support experts love to be a helping hand when you have issues!”",
                            "“Your portable gaming device is covered for Accidental Damage from Handling such as drops, spills, or cracked screens.”",
                        ],
                    },
                ],
            },
            {
                sectionTitle: "Price",
                sectionGroups: [
                    {
                        heading: "“It’s $25 plus tax, per month”",
                        bulletPoints: [],
                    },
                ],
            },
            {
                sectionTitle: "Ask for Sale",
                sectionGroups: [
                    {
                        heading: "“May I start your enrollment?”",
                        bulletPoints: [],
                    },
                ],
            },
            {
                sectionTitle: "Ask to Send Link",
                sectionGroups: [
                    {
                        heading:
                            "“Can I text you a link to review important plan information and complete your enrollment?”",
                        bulletPoints: [],
                    },
                ],
            },
            {
                sectionTitle: "Resolve Concern",
                sectionGroups: [
                    {
                        heading: "Objection: “Too Expensive / Budget”​",
                        bulletPoints: [
                            "“I totally respect that you’re mindful of your budget. While $25 plus tax a month may seem high, the average retail cost to replace things like a TV, PC, laptop, or tablet can be several hundreds of dollars each! With this plan, for one low monthly cost, all of your eligible home tech would be covered if something (or many things) suddenly stopped working. It’s a smart way to protect your financial investments and avoid full retail cost for a repair or replacement.”​",
                        ],
                    },
                    {
                        heading: "Objection: “Need time to Think”​",
                        bulletPoints: [
                            "“Absolutely, I understand wanting to think it through. I just want to highlight why enrolling today matters—your coverage begins 31 days from enrollment, and pre-existing issues aren’t covered. By starting now, your coverage begins in just 31 days, and your home tech will be protected from future breakdowns. I’d love to get you enrolled today and lock in that peace of mind.”​",
                        ],
                    },
                    {
                        heading: "Objection: “Need to Talk to Someone Else”​",
                        bulletPoints: [
                            "“I get that—important decisions are worth discussing. If you’d like, I can help answer any questions you think they might have. I also want to highlight why enrolling today matters—your coverage begins 31 days after enrollment, and pre-existing issues aren’t covered. By starting now, you’ll be protected from future breakdowns. Getting you enrolled today locks in that peace of mind.”​",
                        ],
                    },
                    {
                        heading: "Objection: “I Have the Manufacturer’s Warranty”​",
                        bulletPoints: [
                            "“It’s great you are still under the manufacturer’s warranty but keep in mind that most manufacturer warranties are limited, often only covering defects in material and workmanship for about a year. Our plan goes further—it covers breakdowns from power surges, normal wear and tear, and even accidental damage from handling for certain portable products like laptops or tablets. It provides another level of protection at a very reasonable monthly rate.”​",
                        ],
                    },
                    {
                        heading: "Objection: “I Already Have AppleCare / Similar Product”​",
                        bulletPoints: [
                            "“AppleCare is a great product. What makes Verizon Home Device Protect different is that it covers all your eligible home tech—regardless of brand—with just one low monthly price. So instead of paying for separate plans for each product, you get home tech protection under a single, affordable plan.”​",
                        ],
                    },
                ],
            },
            {
                sectionTitle: "Celebrate Enrollment",
                sectionGroups: [
                    {
                        heading:
                            "“Congratulations on signing up for Verizon Home Device Protect, be on the lookout for a welcome email within the next few days with full details about all of the amazing benefits!”",
                        bulletPoints: [],
                    },
                ],
            },
        ],
        serveSolveContent: [
            {
                sectionTitle: "Greeting",
                sectionGroups: [
                    {
                        heading: "",
                        bulletPoints: [
                            "Warmly greet the customer with a great Tone that creates a Wow Moment!",
                            "Be sure to use the approved verbiage for your team.",
                        ],
                    },
                ],
            },
            {
                sectionTitle: "Show Compassion",
                sectionGroups: [
                    {
                        heading:
                            "Show concern or understanding for customer's issues with phrases that demonstrate empathy, sympathy, or find common ground. Show our customers we understand where they are coming from. Example: “I hear you and definitely understand how important that is.”",
                        bulletPoints: [],
                    },
                ],
            },
            {
                sectionTitle: "Understand Issue",
                sectionGroups: [
                    {
                        heading:
                            "Ask Effective Questions to clearly understand the customer issue. Ask additional probing questions to understand the issue in more depth as needed. Clearly state their issue back to them to make sure you heard them correctly.",
                        bulletPoints: [],
                    },
                ],
            },
            {
                sectionTitle: "Reassure the Customer",
                sectionGroups: [
                    {
                        heading: (
                            <>
                                Use phrases that include power words and show an eagerness to help and instill
                                confidence in our ability to achieve a resolution. <b>AVOID</b> using phrases like, “try
                                my best,” and “see what I can do.” Use confident phrases like,
                                <b>
                                    “You have reached the right person,” ”I will get this taken care of for you.”
                                </b>{" "}
                                This should be done as soon as we understand the customers reason for contacting and
                                throughout the call as needed.
                            </>
                        ),
                        bulletPoints: [],
                    },
                ],
            },
            {
                sectionTitle: "Set Expectations",
                sectionGroups: [
                    {
                        heading:
                            "Let the customer know the steps you will take to solve their issue. Feel free to reassure them again if you think they are still feeling uneasy.",
                        bulletPoints: [],
                    },
                ],
            },
            {
                sectionTitle: "Confirmation of Resolution",
                sectionGroups: [
                    {
                        heading: "",
                        bulletPoints: [
                            "Give the customer all the final details and required information",
                            "Positively summarize how you solved their issue.",
                            "Ask the Customer if they have any questions",
                            "Test / Confirm Resolution",
                        ],
                    },
                ],
            },
        ],
    },
    att: {
        sellClosingContent: [
            {
                sectionTitle: "Thank & Reinforce",
                sectionGroups: [
                    {
                        heading: (
                            <>
                                Leave a lasting positive impression by <b>briefly</b> celebrating the customer getting
                                Enrolled.
                            </>
                        ),
                        bulletPoints: [],
                    },
                ],
            },
            {
                sectionTitle: "Survey",
                sectionGroups: [
                    {
                        heading: "",
                        bulletPoints: [
                            "Be sure to use the approved verbiage for your team.",
                            "Thank the customer for taking the survey, “Thank you for taking the survey (CUSTOMER NAME), It really means a lot to me.”",
                        ],
                    },
                ],
            },
            {
                sectionTitle: "Goodbye with Rapport",
                sectionGroups: [
                    {
                        heading: "",
                        bulletPoints: [
                            "Wish the customer well on whatever rapport you built during the call.",
                            "E.g.: Let’s say you found out the customer was having dinner with friends tonight. The end of the call might sound something like this:",
                            "“Thank you for taking the survey, it means a lot to me. (CUSTOMER NAME), I’m so glad we got you squared away today! And I hope you and your friends have a great time at dinner tonight, have a wonderful rest of your day!",
                        ],
                    },
                ],
            },
        ],
        sellSmartOfferContent: [
            {
                sectionTitle: "Transition Statement",
                sectionGroups: [
                    {
                        heading:
                            "“I'd like to talk to you about a separate coverage from your mobile device insurance called Home Tech Protect.”",
                        bulletPoints: [],
                    },
                ],
            },
            {
                sectionTitle: "Discovery Question",
                sectionGroups: [
                    {
                        heading: "Choose ONE of these options:",
                        bulletPoints: [
                            "“What types of products like, tablets, laptops, or gaming systems does your household use the most?”",
                            "“Have you or your family bought any new tech recently—anything you’re excited about?”​",
                            "“Is there any home tech you’re thinking about upgrading or replacing soon?”",
                        ],
                    },
                ],
            },
            {
                sectionTitle: "Engagement Question",
                sectionGroups: [
                    {
                        heading: "Choose ONE of these options:",
                        bulletPoints: [
                            "“Are those products getting daily use, and what are they mainly used for?”",
                            "“How do you and your family usually use that tech day to day?”​​",
                            "“What do you usually do when your tech stops working?”",
                        ],
                    },
                ],
            },
            {
                sectionTitle: "Core Benefits",
                sectionGroups: [
                    {
                        heading: "",
                        bulletPoints: [
                            "“This plan covers breakdowns on all eligible products for one monthly price no matter when or where they were purchased!”",
                            "“The products you currently own are covered and future purchases are automatically included; No receipts or registration required.”​​",
                            "“A service fee of no more than $99 plus tax is required per claim, and your home tech will be repaired, replaced, or reimbursed.”",
                        ],
                    },
                ],
            },
            {
                sectionTitle: "Examples of Coverage",
                sectionGroups: [
                    {
                        heading: "Choose 1 to 3 to share based on the products mentioned by the customer.",
                        bulletPoints: [
                            "“We’ll cover accidental damage from handling to your eligible laptops for things such as drops, spills, and cracked screens.”",
                            "“We’ll cover your eligible computers for mechanical or electrical failure, and our 24/7 tech support experts love to be a helping hand when you have issues!”",
                            "“We can repair, replace, or reimburse you for your tablet if it experiences a covered breakdown!”",
                            "“We’ll cover your eligible TVs for issues caused by mechanical or electrical failure, and our 24/7 tech support experts love to be a helping hand when you have issues!”",
                            "“Your portable gaming device is covered for Accidental Damage from Handling such as drops, spills, or cracked screens.”",
                        ],
                    },
                ],
            },
            {
                sectionTitle: "Price",
                sectionGroups: [
                    {
                        heading: "“It’s $25 plus tax, per month”",
                        bulletPoints: [],
                    },
                ],
            },
            {
                sectionTitle: "Ask for Sale",
                sectionGroups: [
                    {
                        heading: "“May I start your enrollment?”",
                        bulletPoints: [],
                    },
                ],
            },
            {
                sectionTitle: "Ask to Send Link",
                sectionGroups: [
                    {
                        heading:
                            "“Can I text you a link to review important plan information and complete your enrollment?”",
                        bulletPoints: [],
                    },
                ],
            },
            {
                sectionTitle: "Resolve Concern",
                sectionGroups: [
                    {
                        heading: "Objection: “Too Expensive / Budget”​",
                        bulletPoints: [
                            "“I totally respect that you’re mindful of your budget. While $25 plus tax a month may seem high, the average retail cost to replace things like a TV, PC, laptop, or tablet can be several hundreds of dollars each! With this plan, for one low monthly cost, all of your eligible home tech would be covered if something (or many things) suddenly stopped working. It’s a smart way to protect your financial investments and avoid full retail cost for a repair or replacement.”​",
                        ],
                    },
                    {
                        heading: "Objection: “Need time to Think”​",
                        bulletPoints: [
                            "“Absolutely, I understand wanting to think it through. I just want to highlight why enrolling today matters—your coverage begins 31 days from enrollment, and pre-existing issues aren’t covered. By starting now, your coverage begins in just 31 days, and your home tech will be protected from future breakdowns. I’d love to get you enrolled today and lock in that peace of mind.”​",
                        ],
                    },
                    {
                        heading: "Objection: “Need to Talk to Someone Else”​",
                        bulletPoints: [
                            "“I get that—important decisions are worth discussing. If you’d like, I can help answer any questions you think they might have. I also want to highlight why enrolling today matters—your coverage begins 31 days after enrollment, and pre-existing issues aren’t covered. By starting now, you’ll be protected from future breakdowns. Getting you enrolled today locks in that peace of mind.”​",
                        ],
                    },
                    {
                        heading: "Objection: “I Have the Manufacturer’s Warranty”​",
                        bulletPoints: [
                            "“It’s great you are still under the manufacturer’s warranty but keep in mind that most manufacturer warranties are limited, often only covering defects in material and workmanship for about a year. Our plan goes further—it covers breakdowns from power surges, normal wear and tear, and even accidental damage from handling for certain portable products like laptops or tablets. It provides another level of protection at a very reasonable monthly rate.”​",
                        ],
                    },
                    {
                        heading: "Objection: “I Already Have AppleCare / Similar Product”​",
                        bulletPoints: [
                            "“AppleCare is a great product. What makes Home Tech Protect different is that it covers all your eligible home tech—regardless of brand—with just one low monthly price. So instead of paying for separate plans for each product, you get home tech protection under a single, affordable plan.”​",
                        ],
                    },
                ],
            },
            {
                sectionTitle: "Celebrate Enrollment",
                sectionGroups: [
                    {
                        heading:
                            "“Congratulations on signing up for Home Tech Protect, be on the lookout for a welcome email within the next few days with full details about all of the amazing benefits!”",
                        bulletPoints: [],
                    },
                ],
            },
        ],
        serveSolveContent: [
            {
                sectionTitle: "Greeting",
                sectionGroups: [
                    {
                        heading: "",
                        bulletPoints: [
                            "Warmly greet the customer with a great Tone that creates a Wow Moment!",
                            "Be sure to use the approved verbiage for your team.",
                        ],
                    },
                ],
            },
            {
                sectionTitle: "Show Compassion",
                sectionGroups: [
                    {
                        heading:
                            "Show concern or understanding for customer's issues with phrases that demonstrate empathy, sympathy, or find common ground. Show our customers we understand where they are coming from. Example: “I hear you and definitely understand how important that is.”",
                        bulletPoints: [],
                    },
                ],
            },
            {
                sectionTitle: "Understand Issue",
                sectionGroups: [
                    {
                        heading:
                            "Ask Effective Questions to clearly understand the customer issue. Ask additional probing questions to understand the issue in more depth as needed. Clearly state their issue back to them to make sure you heard them correctly.",
                        bulletPoints: [],
                    },
                ],
            },
            {
                sectionTitle: "Reassure the Customer",
                sectionGroups: [
                    {
                        heading: (
                            <>
                                Use phrases that include power words and show an eagerness to help and instill
                                confidence in our ability to achieve a resolution. <b>AVOID</b> using phrases like, “try
                                my best,” and “see what I can do.” Use confident phrases like,
                                <b>
                                    “You have reached the right person,” ”I will get this taken care of for you.”
                                </b>{" "}
                                This should be done as soon as we understand the customers reason for contacting and
                                throughout the call as needed.
                            </>
                        ),
                        bulletPoints: [],
                    },
                ],
            },
            {
                sectionTitle: "Set Expectations",
                sectionGroups: [
                    {
                        heading:
                            "Let the customer know the steps you will take to solve their issue. Feel free to reassure them again if you think they are still feeling uneasy.",
                        bulletPoints: [],
                    },
                ],
            },
            {
                sectionTitle: "Confirmation of Resolution",
                sectionGroups: [
                    {
                        heading: "",
                        bulletPoints: [
                            "Give the customer all the final details and required information",
                            "Positively summarize how you solved their issue.",
                            "Ask the Customer if they have any questions",
                            "Test / Confirm Resolution",
                        ],
                    },
                ],
            },
        ],
    },
    amazonrouter: {
        serveSolveContent: [],
        sellSmartOfferContent: [
            {
                sectionTitle: "Transition Statement",
                sectionGroups: [
                    {
                        heading:
                            "“Before you go, I do want to quickly share an option that a lot of customers find helpful. It’s called Asurion Complete Protect, and it gives you one simple protection plan for your eligible Amazon purchases.”",
                        bulletPoints: [],
                    },
                ],
            },
            {
                sectionTitle: "Discovery Question",
                sectionGroups: [
                    {
                        heading: "Choose ONE of these options:",
                        bulletPoints: [
                            "“How often do you find yourself shopping on Amazon?”",
                            "“What kind of items do you usually buy on Amazon?”​",
                            "“Do you mostly shop for everyday items or higher-ticket products like electronics?”",
                        ],
                    },
                ],
            },
            {
                sectionTitle: "Engagement Question",
                sectionGroups: [
                    {
                        heading: "Choose ONE of these options:",
                        bulletPoints: [
                            "“Would you say Amazon is your go to before checking out stores?”​",
                            "“Are those products getting daily use, and what are they mainly used for?”​​​",
                            "“ What do you usually do when your tech stops working?”",
                        ],
                    },
                ],
            },
            {
                sectionTitle: "Core Benefits",
                sectionGroups: [
                    {
                        heading: "",
                        bulletPoints: [
                            "“Asurion Complete Protect covers eligible Amazon purchases you’ve made in the last 90 days, and any eligible purchases you make going forward.”",
                            "“Coverage starts right away and covers things like malfunctions or failures and even drops or spills on eligible portable items. If something happens, we will assist you with a repair or reimbursement for the original purchase price.”​​",
                            "“Asurion Complete Protect also includes access to Norton 360 Deluxe, which provides trusted cybersecurity to help keep your devices and personal information safer, including advanced scam detection for emails and texts.”",
                        ],
                    },
                ],
            },
            {
                sectionTitle: "Examples of Coverage",
                sectionGroups: [
                    {
                        heading:
                            "1-3 examples will be surfaced from a bank of statements, matching a product the customer mentioned to a coverage benefit of the home plan.",
                        bulletPoints: [
                            "Example: “Your Laptop would be protected from Accidental Damage from Handling.”",
                        ],
                    },
                ],
            },
            {
                sectionTitle: "Price",
                sectionGroups: [
                    {
                        heading: "“It’s $16.99 plus tax, per month”",
                        bulletPoints: [],
                    },
                ],
            },
            {
                sectionTitle: "Ask for Sale",
                sectionGroups: [
                    {
                        heading: "“May I help you get started with your enrollment?”",
                        bulletPoints: [],
                    },
                ],
            },
            {
                sectionTitle: "Ask to Send Link",
                sectionGroups: [
                    {
                        heading:
                            "“Can I text you a link to review important plan information and complete your enrollment?”",
                        bulletPoints: [],
                    },
                ],
            },
            {
                sectionTitle: "Resolve Concern",
                sectionGroups: [
                    {
                        heading: "Rebuttal verbiage accessible within the tool at any time during the offer",
                        bulletPoints: [],
                    },
                ],
            },
            {
                sectionTitle: "Track the Sale",
                sectionGroups: [
                    {
                        heading:
                            "“Let me know if you have any questions as you review it. Once you subscribe, could you share the order number with me please?”",
                        bulletPoints: [],
                    },
                ],
            },
            {
                sectionTitle: "Celebrate Enrollment",
                sectionGroups: [
                    {
                        heading:
                            "“Congratulations on signing up for Asurion Complete Protect, be on the lookout for a welcome email within the next few days with full details about all of the amazing benefits!”",
                        bulletPoints: [],
                    },
                ],
            },
        ],
        sellClosingContent: [],
    },
};

interface SidebarSectionProps {
    title: string;
    content: {
        sectionTitle: string;
        sectionGroups: {
            heading: string | JSX.Element;
            bulletPoints: string[];
        }[];
    }[];
}

const SidebarSections: FC<SidebarSectionProps> = ({ title, content }) => (
    <Paper
        display="flex"
        shadow="md"
        radius="md"
        w="100%"
        mb="1rem"
        style={{
            background: "var(--mantine-color-dark-5)",
            flexDirection: "column",
        }}
    >
        <Group align="center" gap={8} py="1rem" px="1.25rem" c={"var(--mantine-color-dark-2)"}>
            <IconClipboardList size={20} />
            <Text fz="1rem" fw={700} tt="uppercase">
                {title}
            </Text>
        </Group>
        <Accordion
            defaultValue="Call Checklist"
            chevronPosition="right"
            styles={{
                chevron: { color: "var(--mantine-color-success-4)", scale: 1.2 },
                control: { color: "var(--mantine-color-dark-2)", padding: "1rem 1.5rem" },
                content: { fontSize: "0.875rem", lineHeight: "1rem", padding: "0 1.5rem 1rem" },
                label: { fontSize: "1rem", lineHeight: "1.25rem", fontWeight: 700 },
                item: { borderBottom: "1px solid var(--mantine-color-dark-4)" },
            }}
        >
            {content.map((section) => (
                <Section title={section.sectionTitle} key={section.sectionTitle}>
                    <Accordion.Panel>
                        {section.sectionGroups.map((group) => (
                            <Stack pt="0.5rem" gap="5px" key={group.heading as string}>
                                <Text fz="0.9rem" lh="1.65rem" c="white" mb="0.5rem">
                                    {group.heading}
                                </Text>
                                <div style={{ marginBottom: "0.625rem" }}>
                                    {group.bulletPoints?.map((text) =>
                                        text ? (
                                            <Group
                                                gap="5px"
                                                wrap="nowrap"
                                                justify="top"
                                                align="top"
                                                px="0.25rem"
                                                py="0.5rem"
                                                key={text}
                                            >
                                                <div style={{ paddingTop: 4 }}>
                                                    <UncheckedRadioIcon />
                                                </div>
                                                <Text fz="0.875rem" lh="1.5rem" c="var(--mantine-color-dark-0)">
                                                    {isValidHttpUrl(text) ? (
                                                        <a href={text} target="_blank" rel="noopener noreferrer">
                                                            {text}
                                                        </a>
                                                    ) : (
                                                        text
                                                    )}
                                                </Text>
                                            </Group>
                                        ) : null,
                                    )}
                                </div>
                            </Stack>
                        ))}
                    </Accordion.Panel>
                </Section>
            ))}
        </Accordion>
    </Paper>
);

export const Sidebar = ({ partner }: { partner: AllowedPartners }) => (
    <ScrollArea
        offsetScrollbars
        scrollbarSize={8}
        styles={{
            viewport: { paddingRight: 16 },
        }}
        h="100%"
    >
        {partnerContent[partner]?.serveSolveContent.length > 0 && (
            <SidebarSections title="Serve & Solve" content={partnerContent[partner]?.serveSolveContent} />
        )}
        {partnerContent[partner]?.sellSmartOfferContent.length > 0 && (
            <SidebarSections title="Sell - Smart Offer" content={partnerContent[partner].sellSmartOfferContent} />
        )}
        {partnerContent[partner]?.sellClosingContent.length > 0 && (
            <SidebarSections title="Sell - Closing" content={partnerContent[partner].sellClosingContent} />
        )}
    </ScrollArea>
);

export default Sidebar;
